import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Button, LoadingAnimation, theme, useResponsive } from '@sg/garnish';

import { GiftCardCheckoutFormContainer } from '../GiftCardCheckoutFormContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutFormPolling = (props: GiftCardCheckoutFormPollingProps) => {
  const { isDelayed, isLoading, cancelPayment } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  if (!isDelayed) {
    return (
      <View style={match([styles.containerXS, styles.containerSM])}>
        <GiftCardCheckoutFormContainer style={styles.content}>
          <View style={styles.loading}>
            <LoadingAnimation />
          </View>

          <BodyText sizeMatch={['24']} style={styles.title}>
            <FormattedMessage {...messages.title} />
          </BodyText>
        </GiftCardCheckoutFormContainer>
      </View>
    );
  }
  return (
    <View style={match([styles.containerXS, styles.containerSM])}>
      <GiftCardCheckoutFormContainer style={styles.content}>
        <View style={styles.loading}>
          <LoadingAnimation />
        </View>

        <BodyText sizeMatch={['24']} style={styles.title}>
          <FormattedMessage {...messages.title} />
        </BodyText>

        <BodyText sizeMatch={['16']} style={styles.body}>
          <FormattedMessage {...messages.body} />
        </BodyText>

        <Button
          size="large-wide"
          palette="kaleLightest"
          style={styles.cta}
          accessibilityLabel={formatMessage(messages.cta)}
          isLoading={isLoading}
          onPress={cancelPayment}
        >
          <FormattedMessage {...messages.cta} />
        </Button>
      </GiftCardCheckoutFormContainer>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    height: '100%',
  },
  containerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  content: {
    width: '100%',
    height: '100%',
    padding: theme.spacing['8'],
    marginHorizontal: theme.spacing['4'],
    rowGap: theme.spacing['4'],
    alignSelf: 'center',
  },
  loading: {
    height: 200,
  },
  title: {
    textAlign: 'center',
    color: theme.colors.KALE,
  },
  body: {
    textAlign: 'center',
  },
  cta: {
    minHeight: 64,
    alignItems: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Waiting for payment confirmation...',
    description: 'Gift Card Checkout Form | Polling | Title',
  },
  body: {
    defaultMessage: `This is taking longer than expected.
You might have to confirm the payment again.`,
    description: 'Gift Card Checkout Form | Polling | Body',
  },
  cta: {
    defaultMessage: 'Cancel payment',
    description: 'Gift Card Checkout Form | Polling | CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutFormPollingProps = {
  isDelayed: boolean;
  isLoading: boolean;
  cancelPayment: () => void;
};
